import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { CONFIGURATION_RECORD_ACTIVE } from '../../constants/configuration';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createWfhConfiguration,
  getWfhConfiguration,
  updateWfhConfiguration,
} from '../../services/hrPolicyConfigurationService';
import type { WfhConfiguration } from '../../types/hrPolicyConfiguration';
import { normalizeApiError } from '../../api/apiError';
import { runOperation } from '../../utils/operationFeedback';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhConfigurationCreate'>;

export type WfhConfigurationForm = {
  policyCode: string;
  policyName: string;
  description: string;
  effectiveFrom: string;
  maximumDaysPerMonth: string;
  maximumDaysPerYear: string;
  maximumConsecutiveDays: string;
  minimumNoticeDays: string;
  allowHalfDay: boolean;
  isDefault: boolean;
};

const LIMITS = {
  maximumDaysPerMonth: { label: 'Maximum days per month', max: 31 },
  maximumDaysPerYear: { label: 'Maximum days per year', max: 366 },
  maximumConsecutiveDays: { label: 'Maximum consecutive days', max: 366 },
  minimumNoticeDays: { label: 'Minimum notice days', max: 366 },
} as const;

const today = new Date().toISOString().slice(0, 10);

const EMPTY: WfhConfigurationForm = {
  policyCode: '',
  policyName: '',
  description: '',
  effectiveFrom: today,
  maximumDaysPerMonth: '',
  maximumDaysPerYear: '',
  maximumConsecutiveDays: '',
  minimumNoticeDays: '',
  allowHalfDay: true,
  isDefault: false,
};

export function formFromWfhConfiguration(item: WfhConfiguration): WfhConfigurationForm {
  return {
    policyCode: item.policyCode,
    policyName: item.policyName,
    description: item.description ?? '',
    effectiveFrom: String(item.effectiveFrom).slice(0, 10),
    maximumDaysPerMonth: String(item.maximumDaysPerMonth),
    maximumDaysPerYear: String(item.maximumDaysPerYear),
    maximumConsecutiveDays: String(item.maximumConsecutiveDays),
    minimumNoticeDays: String(item.minimumNoticeDays),
    allowHalfDay: item.allowHalfDay,
    isDefault: item.isDefault,
  };
}

export function validateWfhConfigurationForm(form: WfhConfigurationForm): string | null {
  if (!form.policyCode.trim() || !form.policyName.trim()) {
    return 'Policy code and name are required.';
  }
  if (form.policyCode.trim().length > 50) return 'Policy code is too long.';
  if (form.policyName.trim().length > 150) return 'Policy name is too long.';
  if (form.description.trim().length > 1000) return 'Description is too long.';
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom) ||
    Number.isNaN(Date.parse(`${form.effectiveFrom}T00:00:00Z`))
  ) {
    return 'Effective from must use YYYY-MM-DD.';
  }
  for (const key of Object.keys(LIMITS) as (keyof typeof LIMITS)[]) {
    const parsed = Number(form[key]);
    const { label, max } = LIMITS[key];
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > max) {
      return `${label} must be a whole number from 0 to ${max}.`;
    }
  }
  return null;
}

export function WfhConfigurationCreateScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const wfhPolicyId = route.params?.wfhPolicyId;
  const isEdit = typeof wfhPolicyId === 'number' && wfhPolicyId > 0;
  const [form, setForm] = useState<WfhConfigurationForm>(EMPTY);
  const [rowVersion, setRowVersion] = useState<number | null>(null);
  const [ready, setReady] = useState(!isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (key: keyof WfhConfigurationForm, value: string | boolean) =>
    setForm(current => ({ ...current, [key]: value }));

  const loadPolicy = useCallback(async () => {
    if (!isEdit || wfhPolicyId == null) return;
    setReady(false);
    setLoadError(null);
    try {
      const item = await getWfhConfiguration(wfhPolicyId);
      setForm(formFromWfhConfiguration(item));
      setRowVersion(item.rowVersion);
      setReady(true);
    } catch (caught) {
      setLoadError(normalizeApiError(caught).message || 'Unable to load WFH policy.');
    }
  }, [isEdit, wfhPolicyId]);

  useEffect(() => {
    loadPolicy().catch(() => undefined);
  }, [loadPolicy]);

  async function submit() {
    const validation = validateWfhConfigurationForm(form);
    if (validation) {
      setError(validation);
      return;
    }
    const payload = {
      policyCode: form.policyCode.trim().toUpperCase(),
      policyName: form.policyName.trim(),
      description: form.description.trim() || null,
      effectiveFrom: form.effectiveFrom,
      maximumDaysPerMonth: Number(form.maximumDaysPerMonth),
      maximumDaysPerYear: Number(form.maximumDaysPerYear),
      maximumConsecutiveDays: Number(form.maximumConsecutiveDays),
      minimumNoticeDays: Number(form.minimumNoticeDays),
      allowHalfDay: form.allowHalfDay,
      isDefault: form.isDefault,
    };
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        if (wfhPolicyId == null || rowVersion == null) {
          setError('WFH policy could not be loaded.');
          return;
        }
        await runOperation('update', () =>
          updateWfhConfiguration(wfhPolicyId, {
            ...payload,
            isActive: CONFIGURATION_RECORD_ACTIVE,
            rowVersion,
          }),
        );
      } else {
        await runOperation('add', () => createWfhConfiguration(payload));
      }
      navigation.goBack();
    } catch (caught) {
      setError(normalizeApiError(caught).message || 'Unable to save WFH policy.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PermissionGuard permission={PERMISSIONS.SETTINGS_UPDATE} fallback={<UnauthorizedScreen />}>
      <AppScreen scroll>
        <ScreenHeader
          title={isEdit ? 'Edit WFH policy' : 'Create WFH policy'}
          onBack={navigation.goBack}
        />
        {isEdit && !ready ? (
          loadError ? (
            <ErrorState message={loadError} onRetry={() => loadPolicy().catch(() => undefined)} />
          ) : (
            <AppLoader message="Loading WFH policy" />
          )
        ) : (
          <View style={styles.body}>
            <AppInput
              label="Policy code"
              value={form.policyCode}
              onChangeText={value => set('policyCode', value)}
              autoCapitalize="characters"
            />
            <AppInput
              label="Policy name"
              value={form.policyName}
              onChangeText={value => set('policyName', value)}
            />
            <AppInput
              label="Description"
              value={form.description}
              onChangeText={value => set('description', value)}
            />
            <AppInput
              label="Effective from"
              value={form.effectiveFrom}
              onChangeText={value => set('effectiveFrom', value)}
              placeholder="YYYY-MM-DD"
            />
            <AppInput
              label="Maximum days per month"
              value={form.maximumDaysPerMonth}
              onChangeText={value => set('maximumDaysPerMonth', value)}
              keyboardType="number-pad"
            />
            <AppInput
              label="Maximum days per year"
              value={form.maximumDaysPerYear}
              onChangeText={value => set('maximumDaysPerYear', value)}
              keyboardType="number-pad"
            />
            <AppInput
              label="Maximum consecutive days"
              value={form.maximumConsecutiveDays}
              onChangeText={value => set('maximumConsecutiveDays', value)}
              keyboardType="number-pad"
            />
            <AppInput
              label="Minimum notice days"
              value={form.minimumNoticeDays}
              onChangeText={value => set('minimumNoticeDays', value)}
              keyboardType="number-pad"
            />
            <Row label="Allow half day" value={form.allowHalfDay} onChange={value => set('allowHalfDay', value)} />
            <Row label="Default policy" value={form.isDefault} onChange={value => set('isDefault', value)} />
            {error ? (
              <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
                {error}
              </Text>
            ) : null}
            <AppButton
              title={isEdit ? 'Save WFH policy' : 'Create WFH policy'}
              onPress={submit}
              loading={saving}
              disabled={saving}
            />
          </View>
        )}
      </AppScreen>
    </PermissionGuard>
  );
}

function Row({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  const palette = useAppColors();
  return (
    <View style={styles.row}>
      <Text style={{ color: palette.textPrimary }}>{label}</Text>
      <Switch value={value} onValueChange={onChange} />
    </View>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xl },
  row: { minHeight: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  error: typography.caption,
});
